// ── Report Generator ──

import type { ErlangCResults, ErlangAResults, DayPlannerSummary, ErlangCInputs, ErlangAInputs } from '../types/erlang';

function formatPercent(v: number): string {
  return (v * 100).toFixed(1) + '%';
}

function formatSeconds(v: number): string {
  return v.toFixed(1) + 's';
}

function formatCurrency(v: number): string {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function generateErlangCReport(inputs: ErlangCInputs, results: ErlangCResults): string {
  return `
═══════════════════════════════════════════════════════
  ERLANG C STAFFING REPORT
  Generated by WFM Labs Erlang Suite
  ${new Date().toLocaleString()}
═══════════════════════════════════════════════════════

INPUTS
──────
  Calls per Interval:    ${inputs.callsPerInterval}
  AHT:                   ${inputs.ahtSeconds}s
  Interval Length:       ${inputs.intervalMinutes} min
  Target SL:             ${inputs.targetSLPercent}% in ${inputs.targetAnswerTime}s
  Shrinkage:             ${inputs.shrinkagePercent}%
  Cost per Agent/Hour:   ${formatCurrency(inputs.costPerAgentHour)}

RESULTS
───────
  Traffic Intensity:     ${results.trafficIntensity.toFixed(2)} Erlangs
  Raw Agents Required:   ${results.rawAgents}
  Rostered Agents:       ${results.rosteredAgents}
  Service Level:         ${formatPercent(results.serviceLevel)}
  ASA:                   ${formatSeconds(results.asa)}
  P(Wait):               ${formatPercent(results.probabilityWait)}
  Occupancy:             ${formatPercent(results.occupancy)}
  Immediate Answer:      ${formatPercent(results.immediateAnswer)}
  Avg Queue Length:      ${results.avgQueueLength.toFixed(2)}
  Cost/Interval:         ${formatCurrency(results.costPerInterval)}
  Cost/Hour:             ${formatCurrency(results.costPerHour)}

═══════════════════════════════════════════════════════
  WFM Labs — wfmlabs.com
═══════════════════════════════════════════════════════
`.trim();
}

export function generateErlangAReport(inputs: ErlangAInputs, results: ErlangAResults): string {
  return `
═══════════════════════════════════════════════════════
  ERLANG A (ABANDONMENT) STAFFING REPORT
  Generated by WFM Labs Erlang Suite
  ${new Date().toLocaleString()}
═══════════════════════════════════════════════════════

INPUTS
──────
  Calls per Interval:    ${inputs.callsPerInterval}
  AHT:                   ${inputs.ahtSeconds}s
  Interval Length:       ${inputs.intervalMinutes} min
  Target SL:             ${inputs.targetSLPercent}% in ${inputs.targetAnswerTime}s
  Avg Patience Time:     ${inputs.avgPatienceTime}s
  Shrinkage:             ${inputs.shrinkagePercent}%
  Cost per Agent/Hour:   ${formatCurrency(inputs.costPerAgentHour)}

RESULTS — ERLANG A
──────────────────
  Traffic Intensity:     ${results.trafficIntensity.toFixed(2)} Erlangs
  Raw Agents (Erlang A): ${results.rawAgents}
  Rostered Agents:       ${results.rosteredAgents}
  Service Level:         ${formatPercent(results.serviceLevel)}
  ASA:                   ${formatSeconds(results.asa)}
  P(Wait):               ${formatPercent(results.probabilityWait)}
  Occupancy:             ${formatPercent(results.occupancy)}
  Abandon Rate:          ${formatPercent(results.abandonRate)}
  Calls Abandoned:       ${results.callsAbandoned}
  Cost/Hour:             ${formatCurrency(results.costPerHour)}

COMPARISON — ERLANG C vs A
──────────────────────────
  Erlang C Agents:       ${results.rawAgentsErlangC}
  Erlang A Agents:       ${results.rawAgents}
  Agent Savings:         ${results.agentSavings} agents
  C Overestimates By:    ${results.rawAgentsErlangC > 0 ? formatPercent(results.agentSavings / results.rawAgentsErlangC) : 'N/A'}

═══════════════════════════════════════════════════════
  WFM Labs — wfmlabs.com
═══════════════════════════════════════════════════════
`.trim();
}

export function generateDayPlanReport(summary: DayPlannerSummary, model: string): string {
  const header = `
═══════════════════════════════════════════════════════
  DAY PLANNER STAFFING REPORT (${model.toUpperCase()})
  Generated by WFM Labs Erlang Suite
  ${new Date().toLocaleString()}
═══════════════════════════════════════════════════════

SUMMARY
───────
  Peak Raw Agents:       ${summary.peakRawAgents}
  Peak Rostered Agents:  ${summary.peakRosteredAgents}
  Avg Raw Agents:        ${summary.avgRawAgents.toFixed(1)}
  Total Agent-Hours:     ${summary.totalAgentHours.toFixed(1)}
  Total Cost:            ${formatCurrency(summary.totalCost)}

INTERVAL DETAIL
───────────────
  Time     Calls  Raw  Rostered  SL%      ASA     Occ%
  ─────    ─────  ───  ────────  ─────    ─────   ─────`;

  const rows = summary.intervals.map((r) =>
    `  ${r.time.padEnd(8)} ${String(r.calls).padStart(5)}  ${String(r.rawAgents).padStart(3)}  ${String(r.rosteredAgents).padStart(8)}  ${formatPercent(r.serviceLevel).padStart(5)}  ${formatSeconds(r.asa).padStart(6)}  ${formatPercent(r.occupancy).padStart(6)}`
  );

  return `${header}\n${rows.join('\n')}\n
═══════════════════════════════════════════════════════
  WFM Labs — wfmlabs.com
═══════════════════════════════════════════════════════`.trim();
}

export function downloadReport(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
